import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {clearAuthData, LoginResponse, readAuthData, refreshAuthToken, storeAuthData} from '../services/auth.service';
import {logoutUser} from '../services/user-account.service';
import {AUTH_ERROR_EVENT} from '../utils/api.util';
import {Navigate, useNavigate} from 'react-router-dom';

interface AuthContextType {
    user: LoginResponse | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (userData: LoginResponse) => void;
    refreshToken: (userData: LoginResponse) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({children}) => {
    const [user, setUser] = useState<LoginResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Load cached auth data on mount
    useEffect(() => {
        const loginResponse = readAuthData();
        if (loginResponse) {
            try {
                setUser(loginResponse);
            } catch (error) {
                console.error('Failed to parse cached user data:', error);
                clearAuthData()
                setUser(null);
            }
        }
        setIsLoading(false);
    }, []);

    // Add event listener for authentication errors
    useEffect(() => {
        const handleAuthError = () => {
            console.log("Auth error event detected, silently logging out user");
            // Silently logout without showing errors
            setUser(null);
            clearAuthData();
            // Redirect to login page on auth error with silent parameter
            navigate('/login', {
                replace: true,
                state: { silent: true } // Pass silent state to prevent showing error messages
            });
        };

        // Register event listener
        window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);

        // Cleanup function
        return () => {
            window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
        };
    }, [navigate]);

    const login = (loginResponse: LoginResponse) => {
        setUser(loginResponse);
        storeAuthData(loginResponse);
    };

    const refreshToken = async () => {
        try {
            const response = await refreshAuthToken();
            if (response && response.body) {
                setUser(response.body);
                storeAuthData(response.body);
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error refreshing token:", error);
            return false;
        }
    };

    const logout = async (silent: boolean = false) => {
        try {
            // Only call logoutUser API if this is a user-initiated logout (not a token expiration)
            if (!silent) {
                await logoutUser();
            }
        } catch (error) {
            console.error("Error during logout:", error);
        } finally {
            setUser(null);
            clearAuthData();
        }
    };

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshToken
    };
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
